import React from 'react';
import { CalendarRange } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { DateRangeFilterValue } from '@/components/ui/tableFilterFns';

interface DateRangeFilterProps {
  label: string;
  value: DateRangeFilterValue;
  onChange: (value: DateRangeFilterValue) => void;
}

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  label,
  value,
  onChange,
}) => {
  const isActive = Boolean(value.from || value.to);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={`px-4 py-2 border rounded-md text-sm font-medium h-auto w-auto gap-2 ${
            isActive
              ? 'border-primary-500 text-primary-600 bg-primary-50 hover:bg-primary-50'
              : 'border-[#e9eaeb] text-gray-600 hover:bg-gray-50 bg-white'
          }`}
        >
          <CalendarRange className="w-4 h-4" />
          <span>{label}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 space-y-3" align="start">
        <div className="space-y-1">
          <Label htmlFor={`${label}-from`} className="text-xs text-gray-500">
            From
          </Label>
          <input
            id={`${label}-from`}
            type="date"
            className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm"
            value={value.from ?? ''}
            max={value.to ?? undefined}
            onChange={e => onChange({ ...value, from: e.target.value || null })}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`${label}-to`} className="text-xs text-gray-500">
            To
          </Label>
          <input
            id={`${label}-to`}
            type="date"
            className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm"
            value={value.to ?? ''}
            min={value.from ?? undefined}
            onChange={e => onChange({ ...value, to: e.target.value || null })}
          />
        </div>
        {isActive && (
          <Button
            type="button"
            variant="ghost"
            className="w-full h-auto py-1.5 text-sm text-gray-500"
            onClick={() => onChange({ from: null, to: null })}
          >
            Clear
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
};
